/**
 * Invite Member Page
 * 
 * Admin-only page for inviting new family members
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyAuth } from '../../../contexts/FamilyAuthContext';
import axiosClient from '../../../api/axiosClient';

function InviteMember() {
  const { isAdmin, household } = useFamilyAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'member'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [inviteLink, setInviteLink] = useState('');

  if (!isAdmin) {
    navigate('/family/dashboard');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    
    try {
      setLoading(true);
      
      // Create invite (this would need an invite endpoint in backend)
      // For now, we'll create the user directly
      const response = await axiosClient.post('/api/family/signup', {
        name: formData.name,
        email: formData.email,
        password: Math.random().toString(36).slice(-10), // Temporary password
        household_id: household?.id,
        role: formData.role
      });
      
      setSuccess(true);
      setInviteLink(`mailto:${formData.email}?subject=Einladung zu Hayat Family&body=Hallo ${formData.name}, du wurdest zu Hayat Family eingeladen!`);
      
      setTimeout(() => {
        navigate('/family/admin/members');
      }, 3000);
      
    } catch (err) {
      console.error('Failed to invite member:', err);
      setError(err.response?.data?.detail || 'Fehler beim Einladen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      {/* Header */}
      <div className="bg-[#0D1124]/90 backdrop-blur-xl border-b border-neutral-600/40 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/family/admin/members')}
              className="p-2 hover:bg-neutral-700/50 rounded-lg transition-colors"
            >
              ← Zurück
            </button>
            <div>
              <h1 className="text-2xl font-bold">Mitglied einladen</h1>
              <p className="text-sm text-neutral-400">
                Lade ein neues Familienmitglied ein
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto p-6">
        {success ? (
          <div className="bg-green-500/20 border border-green-500/40 rounded-2xl p-8 text-center">
            <div className="text-5xl mb-4">✅</div>
            <h2 className="text-2xl font-bold mb-2">Mitglied eingeladen!</h2>
            <p className="text-neutral-300 mb-4">
              {formData.name} wurde erfolgreich eingeladen.
            </p>
            <p className="text-sm text-neutral-400">
              Du wirst in Kürze zur Mitgliederverwaltung weitergeleitet...
            </p>
          </div>
        ) : (
          <div className="bg-[#0D1124]/80 backdrop-blur-xl rounded-2xl border border-neutral-600/40 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-500/20 border border-red-500/40 rounded-xl text-red-200">
                  {error}
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-[#050816] border border-neutral-600/40 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#5B4B8A]"
                  placeholder="Max Mustermann"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  E-Mail *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-[#050816] border border-neutral-600/40 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#5B4B8A]"
                  placeholder="max@beispiel.de"
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Rolle *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-3 bg-[#050816] border border-neutral-600/40 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#5B4B8A]"
                >
                  <option value="member">Member (Standard)</option>
                  <option value="admin">Admin</option>
                </select>
                <p className="mt-2 text-xs text-neutral-400">
                  Admins können Einstellungen verwalten und Mitglieder einladen
                </p>
              </div>

              {/* Submit */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/family/admin/members')}
                  className="flex-1 px-6 py-3 bg-neutral-700/50 hover:bg-neutral-700/70 rounded-xl transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-[#5B4B8A] to-[#6DCEE8] hover:opacity-90 rounded-xl font-semibold transition-all disabled:opacity-50"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Einladen...
                    </span>
                  ) : (
                    'Einladen'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default InviteMember;
